import { Component, computed, input, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { VisualsDashboardWidgetSizingEnum } from 'dashboard/enums/visuals/visuals-dashboard-widget-sizing.enum';
import { VisualDashboardWidgetSize } from 'dashboard/interfaces/visuals/list-widget.interface';
import { VisualsDashboardService } from 'dashboard/services/visuals-dashboard/visuals-dashboard.service';

interface SizeOptionViewModel {
    size: VisualsDashboardWidgetSizingEnum;
    isDefault: boolean;
    label: string;
    dimensions: string;
    visualWidth: number;
    visualHeight: number;
}

@Component({
    selector: 'app-visuals-dashboard-size-picker',
    imports: [TranslateModule],
    templateUrl: './visuals-dashboard-size-picker.component.html',
    styleUrl: './visuals-dashboard-size-picker.component.scss',
})
export class VisualsDashboardSizePickerComponent {
    availableSizes = input.required<VisualDashboardWidgetSize[]>();
    initialSelectedSize = input<VisualsDashboardWidgetSizingEnum | null>(null);
    selectedSize = signal<VisualsDashboardWidgetSizingEnum | null>(null);
    emitSelectedSize = output<VisualsDashboardWidgetSizingEnum>();

    defaultSize = computed(() => {
        const sizes = this.availableSizes();
        return sizes.find((size) => size.isDefault)?.size || sizes[0]?.size;
    });

    currentSelectedSize = computed(() => {
        return this.selectedSize() ?? this.initialSelectedSize() ?? this.defaultSize();
    });

    sizeOptionsViewModel = computed<SizeOptionViewModel[]>(() => {
        return this.availableSizes().map((sizeOption) => {
            const { width, height } = this.mapSizeToVisualDimensions(sizeOption.size);
            return {
                size: sizeOption.size,
                isDefault: sizeOption.isDefault,
                label: `dashboard.visuals.sizes.${sizeOption.size.toLowerCase()}`,
                dimensions: this.visualsDashboardService.mapSizeToDimensions(sizeOption.size),
                visualWidth: width,
                visualHeight: height,
            };
        });
    });

    constructor(private visualsDashboardService: VisualsDashboardService) {}

    selectSize(size: VisualsDashboardWidgetSizingEnum, event: Event): void {
        event.stopPropagation();
        this.selectedSize.set(size);
        this.emitSelectedSize.emit(size);
    }

    private mapSizeToVisualDimensions(size: VisualsDashboardWidgetSizingEnum): { width: number; height: number } {
        const baseUnit = 24;
        switch (size) {
            case VisualsDashboardWidgetSizingEnum.SMALL:
                return { width: baseUnit, height: baseUnit };
            case VisualsDashboardWidgetSizingEnum.MEDIUM_ROW:
                return { width: baseUnit * 2, height: baseUnit };
            case VisualsDashboardWidgetSizingEnum.LARGE_ROW:
                return { width: baseUnit * 3, height: baseUnit };
            default:
                return { width: baseUnit, height: baseUnit };
        }
    }
}
